import { http } from '@pendo/http';
import without from 'lodash/without';
import uniq from 'lodash/uniq';
import { ORG_ADMIN_RW } from './constants';

export async function fetchSamlConfigList (orgId) {
    const result = await http.get(`/api/organization/${orgId}/samlconfig`);

    return result.data;
}

export async function createSamlConfig (orgId, configOptions) {
    const result = await http.post(`/api/organization/${orgId}/samlconfig`, configOptions);

    return result.data;
}

export async function deleteSamlConfig (orgId, samlConfigId) {
    await http.delete(`/api/organization/${orgId}/samlconfig/${samlConfigId}`);
}

export async function createSamlConfigProvider (orgId, samlConfigId, configOptions) {
    const result = await http.post(
        `/api/organization/${orgId}/samlconfig/${samlConfigId}/providerconfig`,
        configOptions,
    );

    return result.data;
}

export async function updateSamlConfigProvider (orgId, samlConfigId, providerConfigId, configOptions) {
    const patchOps = {
        patch: Object.keys(configOptions).map((field) => {
            return { op: 'replace', path: `/${field}`, value: configOptions[field] };
        }),
    };
    const result = await http.patch(
        `/api/organization/${orgId}/samlconfig/${samlConfigId}/providerconfig/${providerConfigId}`,
        patchOps,
    );

    return result.data;
}

export async function deleteSamlConfigProvider (orgId, samlConfigId, providerConfigId) {
    await http.delete(`/api/organization/${orgId}/samlconfig/${samlConfigId}/providerconfig/${providerConfigId}`);
}

export async function activateSamlConfigProvider (orgId, samlConfigId, providerConfigId) {
    const result = await http.post(
        `/api/organization/${orgId}/samlconfig/${samlConfigId}/providerconfig/${providerConfigId}/activate`,
    );

    return result.data;
}

export async function deactivateSamlConfigProvider (orgId, samlConfigId, providerConfigId) {
    const result = await http.post(
        `/api/organization/${orgId}/samlconfig/${samlConfigId}/providerconfig/${providerConfigId}/deactivate`,
    );

    return result.data;
}

export async function fetchOrgSummary (orgId) {
    const result = await http.get(`/api/organization/${orgId}/summary`);

    return result.data;
}

export async function fetchSubsSummaryInOrg (orgId) {
    const result = await fetchOrgSummary(orgId);

    return result.subscriptions;
}

export async function fetchSubsInOrg (orgId) {
    const result = await http.get(`/api/organization/${orgId}`);

    return result.data.subscriptions;
}

export async function fetchAppsInSub (orgId, subId) {
    const result = await http.get(`/api/organization/${orgId}/s/${subId}/application`);

    return result.data;
}

export async function fetchDailyMauAcrossSubs (orgId, startTime, endTime) {
    const result = await http.get(`/api/organization/${orgId}/maucounts?startTime=${startTime}&endTime=${endTime}`);

    return result.data;
}

export async function fetchAggregatedMauAcrossSubs (orgId, requestBody) {
    const result = await http.post(`/api/organization/${orgId}/maucounts`, requestBody);

    return result.data;
}

export async function fetchCNameConfigs (orgId, subId) {
    let url = `/api/organization/${orgId}`;

    if (subId) {
        url += `/s/${subId}`;
    }

    url += '/cnameconfig';

    const result = await http.get(url);

    return result.data;
}

export async function createCNameConfig (orgId, subId, newCNameReqObj) {
    const result = await http.post(`/api/organization/${orgId}/s/${subId}/cnameconfig`, newCNameReqObj);

    return result.data;
}

export async function deleteCNameConfig (orgId, subId, cfgId) {
    await http.delete(`/api/organization/${orgId}/s/${subId}/cnameconfig/${cfgId}`);
}

export async function fetchOrgUsers (orgId) {
    const result = await http.get(`/api/organization/${orgId}/user`);

    return result.data;
}

export async function fetchOrgUsersWithRoles (orgId) {
    const result = await http.get(`/api/organization/${orgId}/user?orgRolesOnly=true`);

    return result.data;
}

export async function assignOrgRoles (orgId, user, roles) {
    await http.put(`/api/organization/${orgId}/user/${user.id}/roles`, {
        organizationRoles: roles,
    });
}

export async function grantOrgAdminAccess (orgId, user) {
    const roles = uniq((user.organizationRoles || []).concat(ORG_ADMIN_RW));

    await http.put(`/api/organization/${orgId}/user/${user.id}/roles`, {
        organizationRoles: roles,
    });
}

export async function revokeOrgAdminAccess (orgId, user) {
    const roles = without(user.organizationRoles || [], ORG_ADMIN_RW);
    await http.put(`/api/organization/${orgId}/user/${user.id}/roles`, {
        organizationRoles: roles,
    });
}

export async function promoteOrgAdmin (orgId, userId) {
    return http.post(`/api/organization/${orgId}/orgadminassignment/user/${userId}/promoteadmin`);
}

export async function fetchOrgHasAdmin (orgId) {
    const result = await http.get(`/api/organization/${orgId}/orgadminassignment/hasadmin`);

    return result.data.orgHasAdmin;
}

export async function fetchOrgAdminCandidates (orgId) {
    const result = await http.get(`/api/organization/${orgId}/orgadminassignment/candidates`);

    return result.data;
}

export async function retryDNSJob (orgId, subId, cfgId) {
    const result = await http.post(`/api/organization/${orgId}/s/${subId}/cnameconfig/${cfgId}/startjob`);

    return result.data;
}

export async function addCNameToApp (orgId, subId, appId, cfgId) {
    const result = await http.put(`/api/organization/${orgId}/s/${subId}/a/${appId}/cnameconfig/${cfgId}`);

    return result.data;
}

/*
 * deleteCNameFromApp is expected to return a 204 on success which by definition has no result
 */
export async function deleteCNameFromApp (orgId, subId, appId) {
    await http.delete(`/api/organization/${orgId}/s/${subId}/a/${appId}/cnameconfig`);
}

export async function fetchScimEligibleSubs (orgId, configId) {
    const result = await http.get(`/api/organization/${orgId}/scim/configuration/${configId}/subscriptions`);

    return result.data;
}

export async function fetchIdpGroups (orgId, scimConfigId) {
    const result = await http.get(`/api/organization/${orgId}/scim/configuration/${scimConfigId}/groups`);

    return result.data;
}

export async function fetchIdpGroup (orgId, scimConfigId, groupId) {
    const result = await http.get(`/api/organization/${orgId}/scim/configuration/${scimConfigId}/groups/${groupId}`);

    return result.data;
}

// Retrieves SCIM Configuration for Organization.  If none setup returns 404.
//
// GET api/organization/:orgId/scim/configuration/:id
export async function loadScimConfiguration (orgId) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    try {
        const result = await http.get(`/api/organization/${orgId}/scim/configuration`);
        if (result?.status === 200) {
            return result.data;
        }
    } catch {
        throw new Error('Failed to retrieve SCIM configuration.');
    }
}

// Creates New SCIM Configuration.  ApiKey will be a GUID generated by the server.
// Organization Key will be determined based on the subscription the users is logged into.
// IdpGroupPermissions are set to empty. State is set to set up.
//
// POST /api/organization/:orgId/scim/configuration
export async function createScimConfiguration (orgId) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    try {
        const result = await http.post(`/api/organization/${orgId}/scim/configuration`);
        if (result?.status === 201) {
            return result.data;
        }
    } catch {
        throw new Error('Failed to create SCIM configuration.');
    }
}

// Creates New SCIM Configuration with custom validation (issuer, audience, and JWKS URI).
// POST /api/organization/:orgId/scim/configuration/custom
export async function createScimConfigurationCustomValidation (orgId, issuer, audience, jwksUri) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    if (!issuer) throw new Error('Issuer is required for custom SCIM validation');
    if (!audience) throw new Error('Audience is required for custom SCIM validation');
    if (!jwksUri) throw new Error('JWKS URI is required for custom SCIM validation');

    try {
        const result = await http.post(`/api/organization/${orgId}/scim/configuration/custom`, {
            issuer,
            audience,
            jwksUri,
        });
        if (result?.status === 201) {
            return result.data;
        }
    } catch {
        throw new Error('Failed to create SCIM configuration with custom validation.');
    }
}

// Updates existing SCIM Configuration with custom validation (issuer, audience, and JWKS URI).
// PUT /api/organization/:orgId/scim/configuration/:id/custom
export async function updateScimConfigurationCustomValidation (orgId, configId, issuer, audience, jwksUri) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    if (!configId) throw new Error('Configuration ID needed for this call');
    if (!issuer) throw new Error('Issuer is required for custom SCIM validation');
    if (!audience) throw new Error('Audience is required for custom SCIM validation');
    if (!jwksUri) throw new Error('JWKS URI is required for custom SCIM validation');

    try {
        const result = await http.put(`/api/organization/${orgId}/scim/configuration/custom/${configId}`, {
            issuer,
            audience,
            jwksUri,
        });
        if (result?.status === 200) {
            return result.data;
        }
    } catch {
        throw new Error('Failed to update SCIM configuration with custom validation.');
    }
}

// Regenerate the API Key
//
// PUT /api/organization/:orgId/scim/configuration/:id/apikey
export async function regenerateAPIKey (orgId, configurationId) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    if (!configurationId) throw new Error('Configuration ID needed for this call');
    try {
        const result = await http.put(`/api/organization/${orgId}/scim/configuration/${configurationId}/apikey`);
        if (result?.status === 200) {
            return result.data;
        }
    } catch {
        throw new Error('Failed to regenerate API key.');
    }
}

// Removes given SCIM integration
//
// DELETE /api/organization/:orgId/scim/configuration/:id
export async function removeScimConfiguration (orgId, configurationId) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    if (!configurationId) throw new Error('Configuration ID needed for this call');

    try {
        await http.delete(`/api/organization/${orgId}/scim/configuration/${configurationId}`);
    } catch {
        throw new Error('Failed to delete SCIM configuration.');
    }
}

// Update IdP Group Permissions per subscription
//
// PUT /api/organization/:orgId/scim/configuration/:id/idpgroupspermissions/subscription/:subId
export async function updateIdPGroupPermissionsForSub (orgId, configurationId, subscriptionId, payload) {
    if (!orgId) throw new Error('Organization ID needed for this call');
    if (!configurationId) throw new Error('Configuration ID needed for this call');
    if (!subscriptionId) throw new Error('Subscription ID needed for this call');

    try {
        const result = await http.put(
            `/api/organization/${orgId}/scim/configuration/${configurationId}/idpgroupspermissions/subscription/${subscriptionId}`,
            payload,
        );
        if (result?.status === 200) {
            return result.data;
        }
    } catch (error) {
        const wrappedError = new Error('Failed to update IdP Group Permissions for this subscription');
        wrappedError.response = error?.response;

        throw wrappedError;
    }
}
